import React, { useCallback, useEffect, useState } from "react";
import { useContextStateManagement } from "../../context/ContextApi";
import TicketDetailsDialog from "../TicketDetailsDialog/TicketDetailsDialog";
import "./SupportPage.css";

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In Progress",
  closed: "Closed",
};

const PRIORITY_LABELS = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
};

const MAX_ATTACHMENTS = 5;

const defaultFormState = {
  firstName: "",
  lastName: "",
  email: "",
  issue: "",
  priority: "medium",
  description: "",
  attachments: [],
};

const SupportPage = () => {
  const { PracticeId, createTicket, getMyTickets, getTicketById } =
    useContextStateManagement();
  const [formValues, setFormValues] = useState(defaultFormState);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [attachmentPreviews, setAttachmentPreviews] = useState([]);

  // Object URLs are per-File and only meaningful while that exact File is still attached -
  // rebuilt (and the previous batch revoked) whenever the attachment list itself changes, not
  // just once on mount, so removing/adding a file never leaves a stale or leaked preview URL.
  useEffect(() => {
    const urls = formValues.attachments.map((file) => URL.createObjectURL(file));
    setAttachmentPreviews(urls);
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [formValues.attachments]);

  const loadTickets = useCallback(async () => {
    try {
      setLoadingTickets(true);
      setError("");
      const data = await getMyTickets();
      setTickets(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setError(
        requestError.message || "An error occurred while processing your request",
      );
    } finally {
      setLoadingTickets(false);
    }
  }, [getMyTickets]);

  useEffect(() => {
    if (!PracticeId) {
      setTickets([]);
      setLoadingTickets(false);
      return;
    }
    loadTickets();
  }, [PracticeId, loadTickets]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((currentValue) => ({
      ...currentValue,
      [name]: value,
    }));
  };

  const handleAttachmentChange = (event) => {
    const incoming = Array.from(event.target.files || []);
    const nonImage = incoming.find((f) => !f.type.startsWith("image/"));
    if (nonImage) {
      setError("Only image attachments are allowed");
      return;
    }

    setError("");
    setFormValues((currentValue) => {
      const merged = [...currentValue.attachments, ...incoming].slice(0, MAX_ATTACHMENTS);
      return { ...currentValue, attachments: merged };
    });
    event.target.value = "";
  };

  const handleRemoveAttachment = (index) => {
    setFormValues((currentValue) => ({
      ...currentValue,
      attachments: currentValue.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const createdTicket = await createTicket(formValues);
      setTickets((currentValue) => [createdTicket, ...currentValue]);
      setFormValues(defaultFormState);
      setSuccess(
        "Ticket has been submitted and updates will appear under View Tickets.",
      );
    } catch (requestError) {
      setError(
        requestError.message || "An error occurred while processing your request",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleTicketOpen = async (ticket) => {
    setDialogOpen(true);
    setSelectedTicket(ticket);

    try {
      const hydratedTicket = await getTicketById(ticket.ticketId);
      setSelectedTicket(hydratedTicket);
    } catch (requestError) {
      setError(
        requestError.message || "An error occurred while processing your request",
      );
    }
  };

  return (
    <div className="support-page-shell">
      <div className="support-page-header">
        <h1>Submit Your Support Request</h1>
        <p>
          Get help quickly by creating a support ticket. Our team will respond
          within 24 hours.
        </p>
      </div>

      <div className="support-phi-banner" role="note">
        Please avoid entering any Protected Health Information (PHI).
      </div>

      {error ? <div className="support-feedback support-feedback--error">{error}</div> : null}
      {success ? (
        <div className="support-feedback support-feedback--success">{success}</div>
      ) : null}

      <div className="support-grid">
        <section className="support-card support-card--create-ticket">
          <div className="support-card-header">
            <div>
              <h2>Create New Ticket</h2>
              <p>Fill in the details below</p>
            </div>
          </div>

          <form className="support-ticket-form" onSubmit={handleSubmit}>
            <div className="support-ticket-fields">
            <div className="support-form-grid">
              <input
                className="support-field"
                name="firstName"
                placeholder="First Name"
                value={formValues.firstName}
                onChange={handleInputChange}
                required
              />
              <input
                className="support-field"
                name="lastName"
                placeholder="Last Name"
                value={formValues.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
            <input
              className="support-field"
              name="email"
              placeholder="Email Address"
              value={formValues.email}
              onChange={handleInputChange}
              required
            />
            <div className="support-issue-priority-row">
              <label className="support-field-group">
                <span className="support-field-label">Issue</span>
                <input
                  className="support-field"
                  name="issue"
                  placeholder="Issue"
                  value={formValues.issue}
                  onChange={handleInputChange}
                  required
                />
              </label>
              <label className="support-field-group">
                <span className="support-field-label">Priority</span>
                <select
                  className="support-field support-select-field"
                  name="priority"
                  value={formValues.priority}
                  onChange={handleInputChange}
                  required
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </label>
            </div>
            <textarea
              className="support-field support-textarea"
              name="description"
              placeholder="Issue Description"
              value={formValues.description}
              onChange={handleInputChange}
              required
            />

            <label className="support-upload-box">
              <input
                type="file"
                accept="image/*"
                multiple
                className="support-upload-input"
                onChange={handleAttachmentChange}
                disabled={formValues.attachments.length >= MAX_ATTACHMENTS}
              />
              <span>
                {formValues.attachments.length >= MAX_ATTACHMENTS
                  ? `${MAX_ATTACHMENTS}/${MAX_ATTACHMENTS} images attached (max)`
                  : `Click to add images (${formValues.attachments.length}/${MAX_ATTACHMENTS})`}
              </span>
            </label>

            {formValues.attachments.length > 0 && (
              <ul className="support-attachment-list">
                {formValues.attachments.map((file, index) => (
                  <li key={`${file.name}-${index}`} className="support-attachment-item">
                    {attachmentPreviews[index] ? (
                      <img
                        className="support-attachment-thumb"
                        src={attachmentPreviews[index]}
                        alt={file.name}
                      />
                    ) : null}
                    <span className="support-attachment-name">{file.name}</span>
                    <button
                      type="button"
                      className="support-attachment-remove"
                      onClick={() => handleRemoveAttachment(index)}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
            </div>

            <div className="support-ticket-footer">
              <button
                className="support-submit-button"
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Ticket"}
              </button>
            </div>
          </form>
        </section>

        <section className="support-card support-card--tracker">
          <div className="support-card-header">
            <div>
              <h2>Track Your Tickets</h2>
              <p>View and manage all your support requests</p>
            </div>
          </div>

          <div className="support-ticket-list">
            {loadingTickets ? (
              <div className="support-empty-state">Loading your tickets...</div>
            ) : tickets.length === 0 ? (
              <div className="support-empty-state">
                No support tickets found for your account yet.
              </div>
            ) : (
              tickets.map((ticket) => (
                <button
                  type="button"
                  key={ticket.ticketId}
                  className="support-ticket-card"
                  onClick={() => handleTicketOpen(ticket)}
                >
                  <div className="support-ticket-card-bar" />
                  <div className="support-ticket-card-content">
                    <div>
                      <h3>{ticket.issue || "-"}</h3>
                      <p>{`Ticket #${ticket.ticketId}`}</p>
                      <span>{formatDate(ticket.createdAt)}</span>
                    </div>
                    <div className="ticket-badge-stack">
                      <div
                        className={`ticket-priority-badge priority-${
                          ticket.priority || "medium"
                        }`}
                      >
                        {PRIORITY_LABELS[ticket.priority] || "Medium"}
                      </div>
                      <div className={`ticket-status-badge status-${ticket.status}`}>
                        {STATUS_LABELS[ticket.status] || ticket.status || "-"}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </section>
      </div>

      <TicketDetailsDialog
        open={dialogOpen}
        ticket={selectedTicket}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
};

export default SupportPage;

